import {appUrl} from '@frontend/AppUrl'
import {ResponsiveLayout} from '@frontend/components/ResponsiveLayout'
import {trans} from '@frontend/helpers'
import {useUserData} from '@frontend/hooks'
import {useIsDesktopScreen} from '@frontend/hooks/useIsDesktopScreen'
import {
    useAccountFlagsStore,
    useAuthStore,
} from '@frontend/stores'
import type {UserData} from '@frontend/types/User'
import {
    mdiBank,
    mdiChevronDown,
    mdiCreditCardCheckOutline,
    mdiMessageQuestionOutline,
    mdiMessageTextOutline,
    mdiWalletBifoldOutline,
} from '@mdi/js'
import {useState} from 'react'
import {
    Link,
    NavLink,
} from 'react-router-dom'
import {Card} from 'swayok-react-mdb-ui-kit/components/Card/Card'
import {CardBody} from 'swayok-react-mdb-ui-kit/components/Card/CardBody'
import {CardFooter} from 'swayok-react-mdb-ui-kit/components/Card/CardFooter'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {IconButton} from 'swayok-react-mdb-ui-kit/components/Icon/IconButton'
import {RatingStars} from 'swayok-react-mdb-ui-kit/components/Icon/RatingStars'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {AppConfig} from '../../../AppConfig'
import {useFullUserName} from '../../../hooks/useFullUserName'

// Цена одной звезды рейтинга.
const ratingStarValue: number = 20

// Левое меню в ЛК пользователя с информацией о контракте и ссылками на разделы ЛК.
export function CabinetFinancesMenu() {

    const user: UserData = useUserData()!

    const {
        hasNewChatMessages,
        hasNewSupportTicketReplies,
    } = useAccountFlagsStore()

    const logout = useAuthStore(
        state => state.logout
    )
    const isDesktop = useIsDesktopScreen()

    // Видимость блока со ссылками на разделы ЛК.
    const [
        isFooterVisible,
        setIsFooterVisible,
    ] = useState<boolean>(isDesktop)

    if (!user) {
        return null
    }

    const userName = useFullUserName(true)
    const rating: number = 70 // Todo: обновить когда будет реализовано в CRM.

    const userInfo = (
        <>
            <div className="flex-row-center-vertical flex-wrap pt-3">
                <div className="me-2">
                    <span className="text-muted">
                        {trans().cabinet.side_navigation.client_rating}:
                    </span>
                    <span className="fw-500 ms-1">{rating}%</span>
                </div>
                <RatingStars
                    rating={Math.ceil((rating / ratingStarValue) * 100) / 100}
                    starSize={20}
                />
            </div>
            {user.details.bank && (
                <div className="mt-2">
                    <div className="with-icon-flex">
                        <Icon
                            path={mdiBank}
                            color="muted"
                            className="me-2"
                        />
                        {user.details.bank.name}
                    </div>
                    <div className="with-icon-flex mt-2">
                        <Icon
                            path={mdiWalletBifoldOutline}
                            color="muted"
                            className="me-2"
                        />
                        {user.details.bank.account}
                    </div>
                    {user.details.bank.cardNumber && (
                        <div className="with-icon-flex mt-2">
                            <Icon
                                path={mdiCreditCardCheckOutline}
                                color="muted"
                                className="me-2"
                            />
                            {user.details.bank?.cardNumber}
                        </div>
                    )}
                </div>
            )}
        </>
    )

    const menuContent = (
        <>
            <CardFooter className="cabinet-menu-group">
                <div>
                    <NavLink
                        to={appUrl().cabinet.advancePayments.priceList}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.service_pricing}
                    </NavLink>
                </div>
                <div>
                    <NavLink
                        to={NavigationService.makeUrl(
                            appUrl().cabinet.advancePayments.surveyForm,
                            null,
                            {step: '1'}
                        )}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.client_data}
                    </NavLink>
                </div>
                {AppConfig.features?.advances?.enabled && (
                    <div>
                        <NavLink
                            to={appUrl().cabinet.advancePayments.contract}
                            className="side-nav-link"
                        >
                            {trans().cabinet.side_navigation.active_contract}
                        </NavLink>
                    </div>
                )}
                <div>
                    <NavLink
                        to={appUrl().cabinet.transactions.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.transactions}
                    </NavLink>
                </div>
            </CardFooter>
            <CardFooter className="cabinet-menu-group">
                <div>
                    <NavLink
                        to={appUrl().cabinet.ads.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.my_ads}
                    </NavLink>
                </div>
                <div className="flex-row-between">
                    <NavLink
                        to={appUrl().cabinet.chats.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.my_chats}
                    </NavLink>
                    {hasNewChatMessages && (
                        <Icon
                            path={mdiMessageTextOutline}
                            tooltip={trans().cabinet.side_navigation.has_new_chat_messages}
                            size={20}
                            color="blue"
                        />
                    )}
                </div>
                <div>
                    <NavLink
                        to={appUrl().cabinet.notifications.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.notifications}
                    </NavLink>
                </div>
                <div className="flex-row-between">
                    <NavLink
                        to={appUrl().cabinet.supportTickets.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.support_tickets}
                    </NavLink>
                    {hasNewSupportTicketReplies && (
                        <Icon
                            path={mdiMessageQuestionOutline}
                            tooltip={trans().cabinet.side_navigation.has_new_support_ticket_replies}
                            size={20}
                            color="orange"
                        />
                    )}
                </div>
            </CardFooter>
            <CardFooter className="cabinet-menu-group">
                <div>
                    <NavLink
                        to={appUrl().cabinet.settings.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.settings}
                    </NavLink>
                </div>
                <div>
                    <Link
                        to={appUrl().logout}
                        className="side-nav-link"
                        onClick={e => {
                            e.preventDefault()
                            logout()
                        }}
                    >
                        {trans().cabinet.side_navigation.logout}
                    </Link>
                </div>
            </CardFooter>
        </>
    )

    return (
        <Card id="cabinet-profile-info">
            <CardBody className="py-3 py-lg-4">
                <div className="flex-row-between align-items-center">
                    <div className="flex-1 flex-container-row align-items-center">
                        <div className="profile-avatar">
                            {userName.substring(0, 1)}
                        </div>
                        <div className="ms-3 fw-500">
                            {userName}
                        </div>
                    </div>
                    {!isDesktop && (
                        <IconButton
                            path={mdiChevronDown}
                            rotate={isFooterVisible ? 180 : 0}
                            className="px-2 py-3 flex-shrink-0"
                            onClick={() => setIsFooterVisible(!isFooterVisible)}
                        />
                    )}
                </div>
                <ResponsiveLayout
                    desktop={() => userInfo}
                    tablet={() => (
                        <Collapse
                            show={isFooterVisible}
                            showImmediately
                        >
                            {userInfo}
                        </Collapse>
                    )}
                />
            </CardBody>

            <ResponsiveLayout
                desktop={() => menuContent}
                tablet={() => (
                    <Collapse
                        show={isFooterVisible}
                        showImmediately
                    >
                        {menuContent}
                    </Collapse>
                )}
            />
        </Card>
    )
}
